import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {useCorregirIdentidad} from '@/hooks/bateria/use-bateria-ciclo-vida'
import {translateApiError} from '@/lib/errors'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, type TipoDocumento} from '@/lib/documento'
import {validateCedula} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Algunos 409 traen un `detail` objeto (`cedula_duplicada_en_negocio`, `cedula_en_uso`) que
 * `translateApiError` no sabe leer. */
function motivoEstructurado(err: unknown): {motivo: string; employee_name?: string} | null {
  const detail = (err as {response?: {data?: {detail?: unknown}}})?.response?.data?.detail
  if (detail && typeof detail === 'object' && 'motivo' in detail)
    return detail as {motivo: string; employee_name?: string}
  return null
}

export function CorregirIdentidadDialog({
  businessId,
  evaluacionId,
  paquete,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  paquete: BateriaPaquete
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const corregir = useCorregirIdentidad(businessId, evaluacionId)
  const [nombres, setNombres] = useState('')
  const [apellidos, setApellidos] = useState('')
  const [cedula, setCedula] = useState('')
  const [cedulaError, setCedulaError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conflicto, setConflicto] = useState<{motivo: string; employeeName?: string} | null>(null)
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>('CC')

  function close() {
    setNombres('')
    setApellidos('')
    setCedula('')
    setTipoDocumento('CC')
    setCedulaError(null)
    setError(null)
    setConflicto(null)
    corregir.reset()
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setConflicto(null)
    const cedulaProblem = cedula.trim() ? validateCedula(cedula, tipoDocumento) : null
    setCedulaError(cedulaProblem)
    if (cedulaProblem) return
    if (!nombres.trim() && !apellidos.trim() && !cedula.trim()) {
      setError(t('admin.bateria.corregirIdentidad.sinCambios'))
      return
    }
    corregir.mutate(
      {
        paqueteId: paquete.id,
        payload: {
          nombres: nombres.trim() || undefined,
          apellidos: apellidos.trim() || undefined,
          cedula: cedula.trim() || undefined,
          // El tipo viaja solo con un documento reescrito; si no, un cambio de nombre contaría como
          // corrección de identidad.
          tipo_documento: cedula.trim() ? tipoDocumento : undefined,
        },
      },
      {
        onSuccess: close,
        onError: (err) => {
          const estructurado = motivoEstructurado(err)
          if (estructurado) {
            setConflicto({motivo: estructurado.motivo, employeeName: estructurado.employee_name})
            return
          }
          setError(translateApiError(err))
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-lg" data-testid="corregir-identidad-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.corregirIdentidad.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">
            {t('admin.bateria.corregirIdentidad.intro', {
              nombre: paquete.employee_name,
              cedula: paquete.employee_cedula,
            })}
          </p>
          <div className="space-y-2">
            <Label htmlFor="corregir-identidad-nombres">{t('admin.bateria.corregirIdentidad.nombres')}</Label>
            <Input
              maxLength={tope('NOM')}
              id="corregir-identidad-nombres"
              data-testid="corregir-identidad-nombres"
              value={nombres}
              onChange={(e) => setNombres(e.target.value)}
              placeholder={t('admin.bateria.corregirIdentidad.dejarIgual')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="corregir-identidad-apellidos">{t('admin.bateria.corregirIdentidad.apellidos')}</Label>
            <Input
              maxLength={tope('NOM')}
              id="corregir-identidad-apellidos"
              data-testid="corregir-identidad-apellidos"
              value={apellidos}
              onChange={(e) => setApellidos(e.target.value)}
              placeholder={t('admin.bateria.corregirIdentidad.dejarIgual')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="corregir-identidad-cedula">{t('documento.label')}</Label>
            <div className="flex gap-2">
              <TipoDocumentoSelect
                value={tipoDocumento}
                onChange={(tipo) => {
                  setTipoDocumento(tipo)
                  setCedulaError(null)
                }}
                testId="corregir-identidad-tipo-documento"
              />
              <Input
                id="corregir-identidad-cedula"
                data-testid="corregir-identidad-cedula"
                className="flex-1 font-mono"
                maxLength={DOCUMENTO_MAX[tipoDocumento]}
                value={cedula}
                onChange={(e) => {
                  setCedula(e.target.value)
                  setCedulaError(null)
                }}
                onBlur={() => setCedulaError(cedula.trim() ? validateCedula(cedula, tipoDocumento) : null)}
                aria-invalid={!!cedulaError}
                placeholder={paquete.employee_cedula}
              />
            </div>
            <FieldError error={cedulaError ?? undefined} />
          </div>
          {conflicto?.motivo === 'cedula_duplicada_en_negocio' && (
            <div className="notice-amber rounded-lg px-4 py-2.5 text-sm" data-testid="corregir-identidad-conflicto">
              {t('admin.bateria.corregirIdentidad.cedulaDuplicadaEnNegocio', {nombre: conflicto.employeeName})}
            </div>
          )}
          {conflicto?.motivo === 'cedula_en_uso' && (
            <div className="notice-amber rounded-lg px-4 py-2.5 text-sm" data-testid="corregir-identidad-conflicto">
              {t('admin.bateria.corregirIdentidad.cedulaEnUso')}
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={corregir.isPending}
            submitLabel={t('admin.bateria.corregirIdentidad.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
